import type {
  ClientMessage,
  ServerMessage,
  ServerMessageType,
} from '@/shared/messages.ts';

type Listener<T extends ServerMessageType> = (
  msg: Extract<ServerMessage, { type: T }>,
) => void;

type AnyListener = (msg: ServerMessage) => void;

const TOKEN_STORAGE_KEY = 'bracket.biz:token';
const USER_ID_STORAGE_KEY = 'bracket.biz:userId';

export class BracketWsClient {
  #socket: WebSocket | null = null;
  #userId: string | null = null;
  #listeners = new Map<ServerMessageType, Set<AnyListener>>();
  #anyListeners = new Set<AnyListener>();
  #openWaiters: Array<() => void> = [];

  get userId(): string | null {
    return this.#userId;
  }

  isOpen(): boolean {
    return this.#socket?.readyState === WebSocket.OPEN;
  }

  async connect(): Promise<void> {
    // Tear down any prior socket so a stale CONNECTING/CLOSING handle doesn't
    // hang around. The `socket === this.#socket` guard in the close listener
    // distinguishes our own reconnect from a true cross-tab eviction.
    const prior = this.#socket;
    if (prior && prior.readyState !== WebSocket.CLOSED) {
      try {
        prior.close();
      } catch {
        // ignore
      }
    }

    const { token, userId } = await getOrFetchToken();
    this.#userId = userId;
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${proto}://${location.host}/ws?token=${encodeURIComponent(token)}`;
    const socket = new WebSocket(url);
    this.#socket = socket;

    socket.addEventListener('open', () => {
      for (const waiter of this.#openWaiters) {
        waiter();
      }
      this.#openWaiters = [];
    });
    socket.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      let parsed: ServerMessage;
      try {
        parsed = JSON.parse(event.data) as ServerMessage;
      } catch {
        console.warn('[ws] non-JSON message:', event.data);
        return;
      }
      this.#dispatch(parsed);
    });
    socket.addEventListener('close', (event) => {
      console.log('[ws] closed', event.code, event.reason);
      // 4000 = server kicked this socket because another one took our userId.
      // Only treat that as a cross-tab eviction (and clear the token) if the
      // closing socket is still the *current* one. If we've already moved on
      // to a fresh socket via connect(), this 4000 is just our own reconnect
      // superseding the old socket — leave the token alone.
      if (event.code === 4000 && this.#socket === socket) {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(USER_ID_STORAGE_KEY);
      }
    });
  }

  async whenOpen(): Promise<void> {
    if (this.#socket?.readyState === WebSocket.OPEN) return;
    await new Promise<void>((resolve) => this.#openWaiters.push(resolve));
  }

  send(msg: ClientMessage): void {
    if (!this.#socket || this.#socket.readyState !== WebSocket.OPEN) {
      console.warn('[ws] dropping message, socket not open:', msg);
      return;
    }
    this.#socket.send(JSON.stringify(msg));
  }

  on<T extends ServerMessageType>(type: T, listener: Listener<T>): () => void {
    let set = this.#listeners.get(type);
    if (!set) {
      set = new Set();
      this.#listeners.set(type, set);
    }
    set.add(listener as AnyListener);
    return () => set!.delete(listener as AnyListener);
  }

  onAny(listener: AnyListener): () => void {
    this.#anyListeners.add(listener);
    return () => this.#anyListeners.delete(listener);
  }

  #dispatch(msg: ServerMessage): void {
    const set = this.#listeners.get(msg.type);
    if (set) {
      for (const listener of set) {
        listener(msg);
      }
    }
    for (const listener of this.#anyListeners) {
      listener(msg);
    }
  }
}

async function getOrFetchToken(): Promise<{ token: string; userId: string }> {
  const cachedToken = localStorage.getItem(TOKEN_STORAGE_KEY);
  const cachedUserId = localStorage.getItem(USER_ID_STORAGE_KEY);
  if (cachedToken && cachedUserId) {
    return { token: cachedToken, userId: cachedUserId };
  }
  const res = await fetch('/api/get-token');
  if (!res.ok) throw new Error(`Failed to fetch token: ${res.status}`);
  const data = (await res.json()) as { token: string; userId: string };
  localStorage.setItem(TOKEN_STORAGE_KEY, data.token);
  localStorage.setItem(USER_ID_STORAGE_KEY, data.userId);
  return data;
}
